import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { USERS } from "../data/constants";
import { Cpu, Lock, User, ArrowRight, Sparkles, Eye, EyeOff, Loader2 } from "lucide-react";
import Avatar from "../components/Avatar";
import { BrandLogo } from "../components/BrandLogo";
import { toast } from "../components/ui/sonner";
import api from "../lib/api";

const STATS_FALLBACK = { projects: 91, teams: 4, phases: 7 };

export default function Login() {
  const { t, login, lang, setLang } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(STATS_FALLBACK);
  const zh = lang === "zh";

  useEffect(() => {
    api.get("/public/login-stats").then(({ data }) => { if (data && data.projects) setStats({ ...STATS_FALLBACK, ...data }); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(username, password, remember);
    setLoading(false);
    if (res?.ok) { const next = sp.get("next"); nav(next && next.startsWith("/") && !next.startsWith("//") ? next : "/"); }
    else { setErrMsg(res?.message || ""); setErr(true); }
  };

  const quick = (u) => { setUsername(u.username); setPassword(""); setErr(false); };
  const CRM_DEMO = []; // 演示账号凭据已从前端代码移除（安全要求：不在客户端代码内置任何密码）
  const REAL = [];
  const demoUsers = [...USERS, ...CRM_DEMO, ...REAL];
  // 演示账号默认隐藏（用户要求）；仅 REACT_APP_SHOW_DEMO_ACCOUNTS=1 时显示
  const showDemo = process.env.REACT_APP_SHOW_DEMO_ACCOUNTS === "1";
  const REGION = { la: { zh: "洛杉矶", en: "LA" }, norcal: { zh: "北加州", en: "NorCal" }, sd: { zh: "圣地亚哥", en: "SD" } };
  const tintFor = (r) => ({ la: "#f59e0b", norcal: "#38bdf8", sd: "#34d399" }[r] || "#4F6BFF");

  const STATS = [
    { n: stats.projects, unit: zh ? "个" : "", zh: "总项目 · 在建/交付", en: "Projects · active & delivered", testid: "login-stat-projects" },
    { n: stats.teams, unit: zh ? "组" : "", zh: "工程小组 · 北加/洛杉矶/圣地亚哥", en: "Field teams · NorCal / LA / SD", testid: "login-stat-teams" },
    { n: stats.phases, unit: "/8", zh: "施工阶段 · 标准化", en: "Construction phases · standardized", testid: "login-stat-phases" },
  ];
  const inputCls = "h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-11 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition-all focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20";

  return (
    <div className="min-h-screen w-full ambient-glow text-slate-900 antialiased relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 blueprint-grid opacity-60 pointer-events-none" style={{ maskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse at 30% 40%, black 20%, transparent 75%)" }} />
      <div className="absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full blur-[120px] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(79,107,255,0.22), transparent 70%)" }} />
      <div className="absolute -right-32 -bottom-24 h-[460px] w-[460px] rounded-full blur-[130px] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(123,92,255,0.20), transparent 70%)" }} />

      {/* top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-6 safe-top">
        <div className="hidden sm:inline-flex items-center gap-2 text-xs text-slate-500" data-testid="login-system-status">
          <span className="h-2 w-2 rounded-full bg-emerald-500 pulse-dot" />
          {zh ? "全系统在线 · 云端托管 · HTTPS 加密" : "All systems online · Cloud hosted · HTTPS"}
        </div>
        <div className="sm:hidden"><BrandLogo size={32} compact testid="login-brand-mobile" /></div>
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 text-xs font-semibold shadow-sm" data-no-i18n data-testid="login-lang-toggle">
          <button type="button" data-testid="login-lang-zh" onClick={() => setLang("zh")} aria-pressed={zh} className={`px-3.5 py-1.5 rounded-full transition-colors ${zh ? "bg-brand-grad text-white shadow-brand" : "text-slate-500 hover:text-slate-800"}`}>中</button>
          <button type="button" data-testid="login-lang-en" onClick={() => setLang("en")} aria-pressed={lang === "en"} className={`px-3.5 py-1.5 rounded-full transition-colors ${lang === "en" ? "bg-brand-grad text-white shadow-brand" : "text-slate-500 hover:text-slate-800"}`}>EN</button>
          <button type="button" data-testid="login-lang-es" onClick={() => setLang("es")} aria-pressed={lang === "es"} className={`px-3.5 py-1.5 rounded-full transition-colors ${lang === "es" ? "bg-brand-grad text-white shadow-brand" : "text-slate-500 hover:text-slate-800"}`}>ES</button>
        </div>
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row items-stretch gap-10 lg:gap-16 px-6 sm:px-10 lg:px-16 pt-8 lg:pt-4 pb-10 min-h-[calc(100vh-72px)]">
        {/* ---------- Left hero ---------- */}
        <div className="hidden lg:flex flex-col justify-between flex-1 max-w-2xl py-6">
          <div className="fade-up" style={{ animationDelay: "40ms" }}>
            <BrandLogo size={48} tagline={zh ? "阿里建筑 · AI 管理系统" : "Ali Construction · AI Management System"} testid="login-brand" />
          </div>

          <div className="fade-up" style={{ animationDelay: "120ms" }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full bg-brand-50 border border-brand-100 text-brand-600">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="text-[12px] font-semibold tracking-wide">{zh ? "AI 驱动 · 全流程数字化" : "AI-Driven · End-to-End Digital"}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.15] tracking-tight text-slate-900" data-testid="login-headline" data-no-i18n>
              {zh ? (<>建造更智能的项目<br />从 <span className="brand-text">开工到交付</span> 一站到底</>) : lang === "es" ? (<>Construya proyectos más inteligentes.<br />Del <span className="brand-text">arranque a la entrega</span>, una sola plataforma.</>) : (<>Build smarter projects.<br />From <span className="brand-text">kickoff to handover</span>, one platform.</>)}
            </h1>
            <p className="text-slate-500 text-base mt-5 max-w-xl leading-relaxed" data-testid="login-subtitle">
              {zh ? "阿里建筑构建新生活，AI 建筑项目管理领跑者。在一个工作台串起设计、施工、供应链、财务与客户运营。" : "Ali Construction builds a new way of life — the leader in AI construction management. Design, build, supply chain, finance and CRM in one workspace."}
            </p>

            <div className="grid grid-cols-3 gap-4 mt-8 max-w-xl" data-testid="login-stats">
              {STATS.map((s, i) => (
                <div key={s.testid} data-testid={s.testid} className="kpi-accent bg-white/90 backdrop-blur rounded-2xl border border-slate-200/80 shadow-soft pl-5 pr-4 py-4 fade-up" style={{ animationDelay: `${200 + i * 70}ms` }}>
                  <p className="text-2xl font-extrabold text-slate-900 font-display leading-none">{s.n}<span className="text-sm font-semibold text-slate-400 ml-1">{s.unit}</span></p>
                  <p className="text-[11px] text-slate-500 mt-2 leading-snug">{zh ? s.zh : s.en}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-6 text-[11px] text-slate-400" data-testid="login-compliance">
              <span>{zh ? "ACI · 加州建筑标准" : "ACI · California Building Standards"}</span><span className="text-slate-300">·</span>
              <span>{zh ? "AB1033 合规路径" : "AB1033 compliance path"}</span><span className="text-slate-300">·</span>
              <span>{zh ? "HTTPS 加密传输" : "HTTPS encrypted transport"}</span><span className="text-slate-300">·</span>
              <span>v2.4 · 2026.09</span>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 fade-up" style={{ animationDelay: "300ms" }}>© 2026 Aligcus Inc. · San José · Los Angeles · San Diego</p>
        </div>

        {/* ---------- Right form card ---------- */}
        <div className="flex-1 flex items-center justify-center lg:justify-end">
          <div className="w-full max-w-[420px] relative z-10">
            <div className="rounded-3xl border border-slate-200/80 bg-white/95 backdrop-blur-md p-8 sm:p-9 shadow-[0_24px_60px_-20px_rgba(79,107,255,0.25)] fade-up" style={{ animationDelay: "80ms" }} data-testid="login-card">
              <div className="h-12 w-12 rounded-2xl bg-brand-grad shadow-brand flex items-center justify-center mb-6">
                <Lock className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 font-display tracking-tight">{zh ? "欢迎回来" : "Welcome back"}</h2>
              <p className="text-slate-500 text-sm mt-1 mb-7">{zh ? "登录以进入项目管理工作台" : "Sign in to your project workspace"}</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700">{t("username")}</label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input data-testid="login-username-input" value={username} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
                      onChange={(e) => { setUsername(e.target.value); setErr(false); }} onBlur={() => setUsername((v) => (v || "").trim())}
                      className={inputCls} placeholder={zh ? "用户名 / 邮箱" : "Username / Email"} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">{t("password")}</label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input data-testid="login-password-input" type={showPw ? "text" : "password"} value={password} autoComplete="current-password"
                      onChange={(e) => { setPassword(e.target.value); setErr(false); }}
                      className={inputCls} placeholder="••••••••" />
                    <button type="button" data-testid="login-toggle-password" onClick={() => setShowPw((v) => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label={showPw ? "Hide password" : "Show password"}>
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <label className="inline-flex items-center gap-2 text-slate-600 cursor-pointer select-none">
                    <input type="checkbox" data-testid="login-remember-checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-brand-500" />
                    {zh ? "本机保持登录" : "Keep me signed in"}
                  </label>
                  <button type="button" data-testid="login-forgot-password" onClick={() => toast.info(zh ? "请联系系统管理员重置密码" : "Please contact your administrator to reset your password")} className="text-brand-600 hover:text-brand-700 font-medium">
                    {zh ? "忘记密码？" : "Forgot password?"}
                  </button>
                </div>
                {err && <p data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{errMsg || t("loginError")}</p>}
                {!err && sp.get("expired") && <p data-testid="login-expired" className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{zh ? "登录已过期，请重新登录" : "Your session has expired — please sign in again"}</p>}
                <button data-testid="login-submit-button" type="submit" disabled={loading} className="btn-brand group w-full h-12 text-[15px]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loading ? (zh ? "登录中…" : "Signing in…") : (zh ? "登录到工作台" : "Sign in to workspace")}
                  {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
                </button>
              </form>

              <p className="mt-6 text-xs text-slate-400 flex items-center justify-between" data-testid="login-footer-help">
                <span>{zh ? "还没有工作台账号？" : "No workspace account yet?"}</span>
                <a href="mailto:info@aligcus.com" className="text-brand-600 hover:text-brand-700 font-semibold" data-testid="login-contact-sales">{zh ? "联系销售开通" : "Contact sales"}</a>
              </p>
              <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-3" data-testid="login-legal-links">
                <a href="/sms-terms" className="hover:text-slate-600 underline-offset-2 hover:underline" data-testid="login-link-sms-terms">{zh ? "短信条款" : "SMS Terms"}</a>
                <a href="/privacy" className="hover:text-slate-600 underline-offset-2 hover:underline" data-testid="login-link-privacy">{zh ? "隐私政策" : "Privacy Policy"}</a>
                <a href="/terms" className="hover:text-slate-600 underline-offset-2 hover:underline" data-testid="login-link-terms">{zh ? "用户协议" : "Terms of Service"}</a>
              </p>
            </div>

            {/* demo accounts（正式环境隐藏） */}
            {showDemo && (
            <div className="mt-6" data-testid="demo-accounts-panel">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                <Cpu className="h-3.5 w-3.5 text-brand-500" /> {t("demoAccounts")}
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
                {demoUsers.map((u, i) => (
                  <button key={u.username} data-testid={`demo-account-${u.username}`} onClick={() => quick(u)}
                    className="fade-up flex items-center gap-2.5 text-left px-2.5 py-2 rounded-xl border border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/50 transition-colors"
                    style={{ animationDelay: `${140 + i * 26}ms` }}>
                    <Avatar name={u.name || u.username} size={30} tint={tintFor(u.region)} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-800 truncate">{u.username}</span>
                      <span className="block text-[11px] text-slate-400 capitalize truncate">{u.role}{u.region ? ` · ${REGION[u.region][lang] || u.region}` : (u.team ? ` · ${u.team.replace('team_','')}` : '')}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
